import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// ─── Exact same CSS as ADoctrine — prefix sc- to avoid conflicts ──────────────
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .sc-root {
    background: #f5f5f7;
    color: #1d1d1f;
    min-height: 100vh;
    font-family: Arial, Helvetica, sans-serif;
    -webkit-font-smoothing: antialiased;
    padding-top: 184px;
  }

  /* ── HERO ── */
  .sc-hero {
    width: 90%;
    height: 55vh;
    min-height: 340px;
    position: relative;
    overflow: hidden;
    margin: 0 auto;
    border-radius: 20px;
  }
  .sc-hero img {
    width: 100%; height: 100%;
    object-fit: cover;
    filter: brightness(0.46) saturate(0.8);
    transform: scale(1.04);
    transition: transform 8s ease;
    border-radius: 20px;
  }
  .sc-hero-vignette {
    position: absolute; inset: 0;
    background:
      linear-gradient(to top, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.18) 50%, transparent 100%),
      linear-gradient(to right, rgba(0, 0, 0, 0) 0%, transparent 55%);
  }
  .sc-hero-text {
    position: absolute; bottom: 0; left: 0; right: 0;
    padding: clamp(2rem, 6vw, 4.5rem);
  }
  .sc-hero-kicker {
    font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase;
    color: #60a5fa; margin-bottom: 0.9rem; display: flex; align-items: center; gap: 10px;
  }
  .sc-hero-kicker::before {
    content: ''; display: block;
    width: 28px; height: 1.5px; background: #3b82f6;
  }
  .sc-hero-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(2.8rem, 7.5vw, 6rem);
    font-weight: 400; color: #fff; line-height: 0.92;
    letter-spacing: -0.03em;
  }
  .sc-hero-title em { font-style: italic; color: #60a5fa; }
  .sc-hero-sub {
    font-size: clamp(0.78rem, 1.3vw, 0.9rem);
    color: rgba(255, 255, 255, 1); margin-top: 0.9rem;
    max-width: 460px; line-height: 1.68;
  }

  /* ── HERO mobile ── */
  @media (max-width: 600px) {
    .sc-root {
      padding-top: 0;
    }
    .sc-hero {
      width: 94%;
      height: 200px;
      min-height: unset;
      border-radius: 16px;
      margin: 120px auto 0;
    }
    .sc-hero img {
      border-radius: 16px;
      filter: brightness(0.50) saturate(0.85);
    }
    .sc-hero-text {
      padding: 0.85rem 1rem 1rem;
    }
    .sc-hero-kicker {
      font-size: 7.5px;
      letter-spacing: 0.16em;
      margin-bottom: 0.35rem;
      gap: 6px;
    }
    .sc-hero-kicker::before {
      width: 14px;
      height: 1px;
    }
    .sc-hero-sub {
      font-size: 0.7rem;
      margin-top: 0.28rem;
      line-height: 1.52;
      max-width: 100%;
      color: rgba(255,255,255,0.92);
    }
  }

  /* ── FOUNDATION ── */
  .sc-foundation {
    background: #fff;
    border-top: 0.5px solid rgba(29,29,31,0.07);
    border-bottom: 0.5px solid rgba(29,29,31,0.07);
    padding: clamp(3rem, 6vw, 5rem) clamp(1.5rem, 6vw, 4.5rem);
    position: relative;
    overflow: hidden;
    margin-top: clamp(2rem, 4vw, 3rem);
  }
  .sc-foundation::before {
    content: 'SACRAMENTS';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(5rem, 14vw, 13rem);
    font-weight: 400; color: rgba(29,29,31,0.035);
    white-space: nowrap; pointer-events: none;
    letter-spacing: -0.04em; line-height: 1;
  }
  .sc-foundation-inner {
    max-width: 1080px; margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: clamp(2rem, 5vw, 5rem);
    align-items: start;
    position: relative; z-index: 1;
  }
  @media (max-width: 680px) {
    .sc-foundation-inner { grid-template-columns: 1fr; gap: 1.5rem; }
  }
  .sc-foundation-label {
    font-size: 9px; letter-spacing: 0.26em; text-transform: uppercase;
    color: #3b82f6; margin-bottom: 1rem; display: flex; align-items: center; gap: 8px;
  }
  .sc-foundation-label::before {
    content: ''; display: block;
    width: 20px; height: 1px; background: #3b82f6;
  }
  .sc-foundation-heading {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(2rem, 4vw, 3.4rem);
    font-weight: 400; color: #1d1d1f;
    line-height: 1.0; letter-spacing: -0.025em;
  }
  .sc-foundation-heading em { font-style: italic; color: #2563eb; }
  .sc-foundation-refs {
    display: flex; flex-wrap: wrap; gap: 6px; margin-top: 1.5rem;
  }
  .sc-foundation-ref {
    font-size: 8px; letter-spacing: 0.14em; text-transform: uppercase;
    color: #3b82f6; border: 0.5px solid rgba(59,130,246,0.22);
    background: rgba(59,130,246,0.05);
    padding: 4px 9px; border-radius: 3px;
  }
  .sc-foundation-body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(1.1rem, 1.9vw, 1.4rem);
    line-height: 1.72; color: #3a3a3c;
    border-left: 2px solid #2563eb;
    padding-left: 1.5rem;
  }
  .sc-foundation-body strong {
    color: #1d1d1f; font-weight: 400; font-style: normal;
  }
  .sc-foundation-divider {
    max-width: 1080px; margin: 2.5rem auto 0;
    height: 0.5px; background: rgba(29,29,31,0.07);
    position: relative; z-index: 1;
  }

  /* ── LIST ── */
  .sc-list {
    max-width: 1080px;
    margin: 0 auto;
    padding: 4rem clamp(1rem, 5vw, 3rem) 8rem;
  }
  .sc-list-head {
    display: flex; align-items: center; gap: 1.25rem; margin-bottom: 2.8rem;
  }
  .sc-list-head-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(1.7rem, 3.5vw, 2.8rem);
    font-weight: 400; color: #1d1d1f;
    letter-spacing: -0.02em; line-height: 1; white-space: nowrap;
  }
  .sc-list-head-title em { font-style: italic; color: #2563eb; }
  .sc-list-head-line { flex: 1; height: 0.5px; background: rgba(29,29,31,0.10); }
  .sc-list-head-count {
    font-size: 9px; letter-spacing: 0.18em;
    text-transform: uppercase; color: rgba(29,29,31,0.30); white-space: nowrap;
  }

  /* ── CARD ── */
  .sc-card {
    border-bottom: 0.5px solid rgba(29,29,31,0.09);
  }
  .sc-card:first-of-type { border-top: 0.5px solid rgba(29,29,31,0.09); }

  .sc-row {
    display: grid;
    grid-template-columns: 52px 1fr 200px 38px;
    align-items: center;
    gap: 1.25rem;
    width: 100%; border: none; background: transparent;
    text-align: left; cursor: pointer;
    padding: 0; transition: background 0.2s;
  }
  .sc-row:hover { background: rgba(59,130,246,0.028); }
  .sc-row:hover .sc-card-title { color: #2563eb; }
  .sc-row:hover .sc-thumb img { transform: scale(1.07); filter: brightness(0.78); }

  @media (max-width: 660px) {
    .sc-row { grid-template-columns: 40px 1fr 36px; }
    .sc-thumb { display: none !important; }
  }

  .sc-num {
    font-size: 10px; letter-spacing: 0.12em;
    color: #3b82f6; padding: 1.5rem 0;
    font-variant-numeric: tabular-nums; text-align: right;
  }
  .sc-card-info { padding: 1.5rem 0; min-width: 0; }
  .sc-card-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(1.05rem, 1.9vw, 1.36rem);
    font-weight: 400; color: #1d1d1f;
    letter-spacing: -0.01em; line-height: 1.2;
    transition: color 0.22s ease;
  }
  .sc-card-peek {
    font-size: 0.75rem; color: rgba(29,29,31,0.36);
    margin-top: 0.28rem; line-height: 1.5;
    display: -webkit-box; -webkit-line-clamp: 1;
    -webkit-box-orient: vertical; overflow: hidden;
  }

  .sc-thumb {
    width: 200px; height: 62px;
    border-radius: 7px; overflow: hidden;
    background: #e5e5ea; flex-shrink: 0;
  }
  .sc-thumb img {
    width: 100%; height: 100%; object-fit: cover;
    filter: brightness(0.90) saturate(0.85);
    transition: transform 0.55s cubic-bezier(0.25,0.46,0.45,0.94), filter 0.35s ease;
  }

  .sc-chev {
    width: 34px; height: 34px; border-radius: 50%;
    border: 0.5px solid rgba(29,29,31,0.11);
    background: #fff; color: #3b82f6;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: all 0.32s cubic-bezier(0.16,1,0.3,1);
  }
  .sc-chev.open {
    background: #2563eb; border-color: #2563eb;
    color: #fff; transform: rotate(180deg);
  }
  .sc-chev svg { width: 13px; height: 13px; }

  /* ── PANEL ── */
  .sc-panel {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.46s cubic-bezier(0.4,0,0.2,1);
  }
  .sc-panel.open { grid-template-rows: 1fr; }
  .sc-panel-inner { overflow: hidden; }
  .sc-panel-body {
    padding: 0.5rem 0 3rem 0;
    padding-left: calc(52px + 1.25rem);
  }
  @media (max-width: 660px) { .sc-panel-body { padding-left: calc(40px + 1rem); } }

  .sc-detail {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 2.5rem;
    align-items: start;
  }
  @media (max-width: 780px) {
    .sc-detail { grid-template-columns: 1fr; }
  }

  .sc-detail-img-wrap {
    width: 320px; max-width: 100%;
    border-radius: 14px; overflow: hidden;
    position: relative; background: #d1d1d6;
    aspect-ratio: 4 / 3; flex-shrink: 0;
  }
  @media (max-width: 780px) { .sc-detail-img-wrap { width: 100%; } }
  .sc-detail-img-wrap img {
    width: 100%; height: 100%;
    object-fit: cover;
    filter: brightness(0.82) saturate(0.88);
    transform: scale(1.04);
    transition: transform 0.65s cubic-bezier(0.25,0.46,0.45,0.94);
    display: block;
  }
  .sc-panel.open .sc-detail-img-wrap img { transform: scale(1); }
  .sc-detail-img-vignette {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.52) 0%, transparent 55%);
  }
  .sc-detail-img-label {
    position: absolute; bottom: 0.9rem; left: 1rem;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 1.1rem; font-weight: 400; color: #fff;
    letter-spacing: -0.01em; pointer-events: none; line-height: 1.2;
  }
  .sc-detail-img-label em { font-style: italic; color: #93c5fd; }

  .sc-detail-text p {
    font-size: 0.875rem; line-height: 1.78;
    color: #3a3a3c; margin-bottom: 0.85rem;
  }
  .sc-detail-text p:last-child { margin-bottom: 0; }

  .sc-scripture {
    margin-top: 1.5rem;
    display: flex; gap: 1rem; align-items: flex-start;
    padding: 0.9rem 1.1rem;
    background: rgba(59,130,246,0.04);
    border-left: 2px solid #3b82f6;
    border-radius: 0 7px 7px 0;
  }
  .sc-scripture-dot {
    width: 4px; height: 4px; border-radius: 50%;
    background: #3b82f6; margin-top: 5px; flex-shrink: 0;
  }
  .sc-scripture-label {
    font-size: 8px; letter-spacing: 0.22em; text-transform: uppercase;
    color: #3b82f6; display: block; margin-bottom: 0.35rem;
  }
  .sc-scripture-text {
    font-size: 0.76rem; line-height: 1.65; color: rgba(29,29,31,0.44);
  }

  /* ── LEARN MORE LINK ── */
  .sc-learn-wrap {
    max-width: 1080px; margin: 0 auto;
    padding: 0 clamp(1rem, 5vw, 3rem) 2rem;
    display: flex; justify-content: center;
  }
  .sc-learn-btn {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
    color: #2563eb; text-decoration: none;
    border: 0.5px solid rgba(59,130,246,0.28);
    background: rgba(59,130,246,0.05);
    padding: 10px 22px; border-radius: 4px;
    transition: all 0.22s ease;
  }
  .sc-learn-btn:hover { background: #2563eb; color: #fff; border-color: #2563eb; }
  .sc-learn-btn svg { width: 11px; height: 11px; }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const sacraments = [
  {
    question: "Baptism",
    answer: `Repentance and baptism of the Holy Spirit is the gateway to salvation, and into The Kingdom Of God. Through Baptism, the gift of the Holy Spirit is bestowed upon you. It is conducted at any time or day, for all ages.`,
    a: `Baptism is a passage rite into God's Kingdom now established on earth. All those seeking the kingdom of God must be baptised by immersion, notwithstanding whether they had been baptised elsewhere. It can be conducted anywhere in the world — once a convert is ready, it should not be postponed.`,
    b: `Prospective members must denounce all secret societies, do away with charms, rings, occult books and practices before baptism. Money is never charged for baptism. The day of baptism is a unique and most important day — a day of salvation and great joy.`,
    o: `Matthew 3:11 · Matthew 28:19 · John 3:5 · Mark 16:16 · Ephesians 4:4-5`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FUniform.jpg?alt=media&token=0ee50ac7-99df-492e-bfc3-41aace3978fe",
  },
  {
    question: "Child Blessing",
    answer: `Child blessing is an acceptable sacrament in Brotherhood. It is a form of acknowledgement and acceptance of God's gift in the form of a child — with emphasis on the eighth day, as our Lord Jesus Christ was presented in the temple.`,
    a: `It is mandatory that every child in Brotherhood of the Cross and Star be blessed. It is done during morning or evening prayers with strong emphasis on the eighth day. There is an order for the conduction of child blessing as set down by the Holy Spirit.`,
    o: `Luke 2:21-24 · Mark 10:13-16 · Matthew 19:14`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Fkneel.jpg?alt=media&token=c7a78bc0-1bc2-4044-b414-cdcff3288d8d",
  },
  {
    question: "Marriage Blessing",
    answer: `Marriage blessing is one of the sacraments in Brotherhood. Solemnization of marriage in Brotherhood is done by the Holy Spirit — the highest anyone can desire to experience. The Father advises all brethren to have their marriage blessed.`,
    a: `Marriage is recommended for all brethren on one condition: that a man be husband to one wife, and a woman be wife to one husband. Trial and contract marriages are not allowed. Once a marriage is blessed, it cannot be dissolved except on grounds of adultery or demise of a partner.`,
    b: `Any man or woman who does not practice strict monogamy should remain celibate. All married couples are advised to read "Key to Successful Marriage" — sermons by Leader Olumba Olumba Obu.`,
    o: `Matthew 19:4-6 · 1 Corinthians 7:2 · Genesis 2:24 · Ephesians 5:22-25`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FGreetings.jpg?alt=media&token=607618c5-3c17-4bae-98ce-e874076b9de7",
  },
  {
    question: "Love Feast",
    answer: `Love feast is a sacrament introduced by the Holy Spirit in Brotherhood. It commemorates the Passover last supper during which our God and Saviour changed bread to His living Body and wine to His priceless Blood. We daily celebrate the Holy Communion or Love Feast.`,
    a: `The love feast is very sacred and can accomplish anything. Feast is not food to be eaten to be filled — a piece of bread, biscuit, banana, orange, paw-paw or a spoonful of rice is enough. It should be bought, prepared and eaten with utmost reverence to the Holy Spirit.`,
    b: `When feast is served, everyone should kneel down when they collect it. Feast consists of fruits, vegetables, rice and water — purely vegetarian, blessed and served after service or fasting. BCS members do not eat meat, fish, dairy products, or drink alcohol. Acts 2:42-47.`,
    o: `Acts 2:42-47 · Luke 22:19-20 · 1 Corinthians 11:23-26 · Genesis 1:29`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Feva.jpg?alt=media&token=70761a90-695e-46e4-8904-3013b17c8904",
  },
];

// ─── Card — exact DocCard pattern ─────────────────────────────────────────────
function SacCard({ data, index, isOpen, onToggle }) {
  const keys  = ["answer","a","b","c","d","g","h","i","j","k","l","m","n"];
  const paras = keys.map(k => data[k]).filter(Boolean);

  const words      = data.question.split(" ");
  const labelStart = words.slice(0, -1).join(" ");
  const labelEnd   = words.slice(-1)[0];

  return (
    <div className="sc-card">
      <button className="sc-row" onClick={onToggle} aria-expanded={isOpen}>
        <span className="sc-num">{String(index + 1).padStart(2, "0")}</span>
        <div className="sc-card-info">
          <p className="sc-card-title">{data.question}</p>
          <p className="sc-card-peek">{data.answer}</p>
        </div>
        {data.image && (
          <div className="sc-thumb">
            <img src={data.image} alt={data.question} loading="lazy" />
          </div>
        )}
        <div className={`sc-chev ${isOpen ? "open" : ""}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>

      <div className={`sc-panel ${isOpen ? "open" : ""}`}>
        <div className="sc-panel-inner">
          <div className="sc-panel-body">
            <div className="sc-detail">

              {data.image && (
                <div className="sc-detail-img-wrap">
                  <img src={data.image} alt={data.question} loading="lazy" />
                  <div className="sc-detail-img-vignette" />
                  <div className="sc-detail-img-label">
                    {labelStart && <>{labelStart} </>}
                    <em>{labelEnd}</em>
                  </div>
                </div>
              )}

              <div className="sc-detail-text">
                {paras.map((p, i) => <p key={i}>{p.trim()}</p>)}
                {data.o && (
                  <div className="sc-scripture">
                    <div className="sc-scripture-dot" />
                    <div>
                      <span className="sc-scripture-label">Scripture References</span>
                      <p className="sc-scripture-text">{data.o}</p>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const Sacrament = () => {
  const [open, setOpen] = useState(null);
  const toogle = (index) => setOpen(prev => prev === index ? null : index);

  return (
    <>
      <style>{CSS}</style>
      <motion.div
        className="sc-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      >

        {/* ── HERO ── */}
        <div className="sc-hero">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FSacrament.jpg?alt=media&token=30232e47-c003-42e4-851f-990e486fdcdf"
            alt="BCS Sacraments"
          />
          <div className="sc-hero-vignette" />
          <div className="sc-hero-text">
            <span className="sc-hero-kicker">Brotherhood of the Cross and Star</span>
            {/* <h1 className="sc-hero-title">The <em></em></h1> */}
            <p className="sc-hero-sub">
              Certain rites are performed according to the directives of The Holy
              Spirit  Baptism, Child Blessing, Love Feast, and Marriage Blessing.
            </p>
          </div>
        </div>

        {/* ── FOUNDATION ── */}
        <div className="sc-foundation">
          <div className="sc-foundation-inner">
            <div>
              <p className="sc-foundation-label">Holy Rites</p>
              <h2 className="sc-foundation-heading">
                The<br /><em>Sacred</em><br />Rites
              </h2>
              <div className="sc-foundation-refs">
                {["Acts 2:38", "John 3:5", "1 Cor. 11:24", "Gen. 1:29"].map(r => (
                  <span key={r} className="sc-foundation-ref">{r}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="sc-foundation-body">
                In Brotherhood Of The Cross And Star, <strong>certain rites are performed</strong> according
                to the directives of The Holy Spirit. These sacred sacraments are not mere tradition —
                they are <strong>divine ordinances</strong> ordained by God Himself, serving as
                gateways into His kingdom and channels of His grace, peace, and blessing upon
                every obedient soul.
              </p>
            </div>
          </div>
          <div className="sc-foundation-divider" />
        </div>

        {/* ── SACRAMENTS LIST ── */}
        <div className="sc-list">
          <div className="sc-list-head">
            <h2 className="sc-list-head-title">The <em>Sacraments</em></h2>
            <div className="sc-list-head-line" />
            <span className="sc-list-head-count">{sacraments.length} Sacraments</span>
          </div>

          {sacraments.map((data, index) => (
            <SacCard
              key={index}
              data={data}
              index={index}
              isOpen={open === index}
              onToggle={() => toogle(index)}
            />
          ))}
        </div>

        {/* ── LEARN MORE ── */}
        <div className="sc-learn-wrap">
          <Link
            to="/Events"
            onClick={() => window.scrollTo(0, 0)}
            className="sc-learn-btn"
          >
            Upcoming Events
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

      </motion.div>
    </>
  );
};

export default Sacrament;
